import { dischargePD } from '../../conversion/discharge';
import { configuration, drainageChimneyPD } from '../../conversion/drainageChimney';
import { drainageTrenchPD, drainHeight, drainPosition, drainUnitWidth, hasDrainageChimney, hasDrainagePiping, productType } from '../../conversion/drainageTrench';
import { drainPipeDiameterPD } from '../../conversion/drainPipeDiameter';
import { horizontalSeismicLoad, loadPD, minFsSeismicConditions, verticalSeismicLoad } from '../../conversion/load';
import { slopePD } from '../../conversion/slope';
import { drainedSoilPermeability, hydraulicConductivity, layerNumber, layersInSeries, remodeledSoilPermeability, soilInputDataPD, soilPD, soilType, thickness } from '../../conversion/soil';
import i18n, { getLanguage } from '../../localization';
import { redux } from '../../store';
import { ITable, ITableRow } from '../component/Table';
import { EDischargeType, EPosition, EUnitOfMeasure } from '../enum';
import {
    IDischarge,
    IDrainageChimney,
    IDrainageTrench,
    IDrainPipe,
    IInputDischarge,
    IInputDrainageChimney,
    IInputDrainageTrench,
    IInputDrainPipeDiameter,
    IInputLoad,
    IInputSlope,
    IInputSoil,
    ILoad,
    ISlope,
    ISoil,
    ISoilInputData,
    TInputDischarge,
    TInputDrainageChimney,
    TInputDrainageTrench,
    TInputDrainPipeDiameter,
    TInputLoad,
    TInputSlope,
    TInputSoil,
} from '../iProject';

const createColumns = (initialValue: string[], condition?: boolean, addElement?: string) => (condition ? [...initialValue, addElement!] : initialValue);

export const getInputSlope = (slope: ISlope): IInputSlope => ({
    slopeWidth: slope.slopeWidth,
    slopeLength: slope.slopeLength,
    slopeAngle: slope.slopeAngle,
    transverseSlopeAngle: slope.transverseSlopeAngle,
    initialWaterTableDepth: slope.initialWaterTableDepth,
    impermeableLayerDepth: slope.impermeableLayerDepth,
});

export const getSlopeTable = (slope: ISlope): ITable => {
    const project = redux.getState().project;
    let values = getInputSlope(slope);
    let columns = createColumns([i18n.t('slopeGeometryData'), '']);
    let input = Object.keys(values);
    let rows = input.map(x => ({
        label: i18n.t(x, {
            unitOfMeasure: slopePD[x as TInputSlope].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
        }),
        values: [
            {
                value: typeof values[x as TInputSlope]! != 'boolean' ? values[x as TInputSlope]! : values[x as TInputSlope]! ? i18n.t('yes') : i18n.t('no'),
            },
        ],
    }));

    return { columns, rows };
};

export const getInputLoad = (load: ILoad): IInputLoad => ({
    distributedOverload: load.distributedOverload,
    hasSeismicLoads: load.hasSeismicLoads,
    horizontalSeismicLoad: load.horizontalSeismicLoad,
    verticalSeismicLoad: load.verticalSeismicLoad,
    minFsStaticConditions: load.minFsStaticConditions,
    minFsSeismicConditions: load.minFsSeismicConditions,
    drainEfficiency: load.drainEfficiency,
});

export const getLoadTable = (load: IInputLoad): ITable => {
    const project = redux.getState().project;
    let values = getInputLoad(load);
    let columns = createColumns([i18n.t('loads'), '']);
    let input = Object.keys(values).filter(x => (x == horizontalSeismicLoad || x == verticalSeismicLoad || x == minFsSeismicConditions ? values.hasSeismicLoads : true));
    let rows = input.map(x => ({
        label: i18n.t(x, {
            unitOfMeasure: loadPD[x as TInputLoad].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
        }),
        values: [
            {
                value: typeof values[x as TInputLoad]! != 'boolean' ? values[x as TInputLoad]! : values[x as TInputLoad]! ? i18n.t('yes') : i18n.t('no'),
            },
        ],
    }));

    return { columns, rows };
};

export const getInputDicharge = (discharge: IDischarge, type?: EDischargeType): IInputDischarge => {
    let input = {};
    let inputSimple = {};
    let inputCorrection = {
        catchmentArea: discharge.catchmentArea,
        mainChannelLength: discharge.mainChannelLength,
        averageCatchmentElevation: discharge.averageCatchmentElevation,
        closureSectionElevation: discharge.closureSectionElevation,
        returnPeriod: discharge.returnPeriod,
        parameterA: discharge.parameterA,
        parameterN: discharge.parameterN,
        flowConcentrationTime: discharge.flowConcentrationTime,
        rainfallHeight: discharge.rainfallHeight,
        curveNumber: discharge.curveNumber,
    };
    if (type !== undefined) {
        if (type == EDischargeType.Simple) input = { ...inputSimple, totalDischargeToBeDrained: discharge.totalDischargeToBeDrained };
        else input = { ...inputCorrection, totalDischargeToBeDrained: discharge.totalDischargeToBeDrained };
    } else {
        input = { dischargeType: discharge.dischargeType, ...inputSimple, ...inputCorrection, totalDischargeToBeDrained: discharge.totalDischargeToBeDrained };
    }
    return input;
};

export const getDischargeTable = (discharge: IDischarge, type?: EDischargeType): ITable => {
    const project = redux.getState().project;
    let values = getInputDicharge(discharge, type);
    let labelType = type == EDischargeType.Simple ? i18n.t('totalDrainageDischargeOpt') : i18n.t('globalFlowCalculation');
    let columns = createColumns([labelType, '']);
    let input = Object.keys(values);
    let rows = input.map(x => ({
        label: i18n.t(x, {
            unitOfMeasure: dischargePD[x as TInputDischarge].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
        }),
        values: [
            {
                value: typeof values[x as TInputDischarge]! != 'boolean' ? values[x as TInputDischarge]! : values[x as TInputDischarge]! ? i18n.t('yes') : i18n.t('no'),
            },
        ],
    }));

    return { columns, rows };
};

export const getInputDrainageTrench = (drainageTrench: IDrainageTrench): IInputDrainageTrench => ({
    productType: drainageTrench.product!.translations?.find(x => x.iso == getLanguage())?.name,
    drainPositionDepth: drainageTrench.drainPositionDepth,
    expectedWaterTableDepth: drainageTrench.expectedWaterTableDepth,
    drainLineLength: drainageTrench.drainLineLength,
    drainSingle: drainageTrench.drainSingle,
    wheelbase: drainageTrench.wheelbase,
    hasWaterproofMembraneBelowDrain: drainageTrench.hasWaterproofMembraneBelowDrain,
    heightOfSaturatedWaterTableBelowDrains: drainageTrench.heightOfSaturatedWaterTableBelowDrains,
    drainPosition: drainageTrench.drainPosition,
    drainHeight: drainageTrench.product!.productPhysicalDatas![0].drainHeight,
    drainUnitWidth: drainageTrench.product!.productPhysicalDatas![0].drainUnitWidth,
    drainUnitLength: drainageTrench.product!.productPhysicalDatas![0].drainUnitLength,
    numberOfDrains: drainageTrench.numberOfDrains,
    numberOfDrainLines: drainageTrench.numberOfDrainLines,
    waterTableLoweringThickness: drainageTrench.waterTableLoweringThickness,
    hasDrainagePiping: drainageTrench.product!.hasPipe,
    fillingPercentage: drainageTrench.fillingPercentage,
    roughnessCoefficient: drainageTrench.roughnessCoefficient,
    hasDrainageChimney: drainageTrench.hasDrainageChimney,
});

export const getInputDrainageChimney = (drainageChimney: IDrainageChimney): IInputDrainageChimney => ({
    configuration: drainageChimney.configuration,
    drainageChimneyHeight: drainageChimney.drainageChimneyHeight,
    drainageChimneyWidth: drainageChimney.drainageChimneyWidth,
    drainageChimneyLength: drainageChimney.drainageChimneyLength,
    coverLayer: drainageChimney.coverLayer,
    chimneyWheelbase: drainageChimney.chimneyWheelbase,
});

export const getInputPipeDiamater = (drainPipe: IDrainPipe): IInputDrainPipeDiameter => ({
    diameter: drainPipe.drainPipeDiameters![0].diameter,
    internalDiameter: drainPipe.drainPipeDiameters![0].internalDiameter,
});

const positionLabel = {
    [EPosition.Vertical]: i18n.t('vertical'),
    [EPosition.Horizontal]: i18n.t('horizontal'),
};

export const getDrainageTrenchTable = (drainageTrench: IDrainageTrench[]): ITable => {
    const project = redux.getState().project;
    let columns = createColumns(['', i18n.t('primaryCollector')], drainageTrench.length > 1, i18n.t('secondaryCollector'));

    let valuesPrimaryDrainageTrench = getInputDrainageTrench(drainageTrench[0]);
    let valuesPrimaryPipeDiamater = getInputPipeDiamater(drainageTrench[0].drainPipe!);
    let valuesPrimaryDrainageChimney = getInputDrainageChimney(drainageTrench[0].drainageChimney!);

    let valuesSecondaryDrainageTrench = undefined;
    let valuesSecondaryPipeDiamater = undefined;
    let valuesSecondaryDrainageChimney = undefined;
    if (drainageTrench.length > 1) {
        valuesSecondaryDrainageTrench = getInputDrainageTrench(drainageTrench[1]);
        valuesSecondaryPipeDiamater = getInputPipeDiamater(drainageTrench[1].drainPipe!);
        valuesSecondaryDrainageChimney = getInputDrainageChimney(drainageTrench[1].drainageChimney!);
    }

    let inputDrainageTrench = Object.keys(valuesPrimaryDrainageTrench);
    let inputPipeDiamater = Object.keys(valuesPrimaryPipeDiamater);
    let inputDrainageChimney = Object.keys(valuesPrimaryDrainageChimney);
    let rows: ITableRow[] = [];

    inputDrainageTrench.map(x => {
        let valuePrimary = valuesPrimaryDrainageTrench[x as TInputDrainageTrench]!;
        switch (x) {
            case drainPosition:
                valuePrimary = positionLabel[valuePrimary as EPosition];
                break;
            case drainHeight:
                if (valuesPrimaryDrainageTrench[drainPosition] == EPosition.Horizontal) valuePrimary = valuesPrimaryDrainageTrench[drainUnitWidth] as number;
                break;
            case drainUnitWidth:
                if (valuesPrimaryDrainageTrench[drainPosition] == EPosition.Horizontal) valuePrimary = valuesPrimaryDrainageTrench[drainHeight] as number;
                break;
            default:
                break;
        }
        let row: ITableRow = {
            label: i18n.t(x, {
                unitOfMeasure: drainageTrenchPD[x as TInputDrainageTrench].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
            }),
            values: [
                {
                    value: typeof valuePrimary != 'boolean' ? valuePrimary : valuePrimary ? i18n.t('yes') : i18n.t('no'),
                },
            ],
            isBold: x == productType || x == hasDrainagePiping || x == hasDrainageChimney,
        };
        if (valuesSecondaryDrainageTrench) {
            let valueSecondary = valuesSecondaryDrainageTrench![x as TInputDrainageTrench]!;
            switch (x) {
                case drainPosition:
                    valueSecondary = positionLabel[valueSecondary as EPosition];
                    break;
                case drainHeight:
                    if (valuesSecondaryDrainageTrench[drainPosition] == EPosition.Horizontal) valueSecondary = valuesSecondaryDrainageTrench[drainUnitWidth] as number;
                    break;
                case drainUnitWidth:
                    if (valuesSecondaryDrainageTrench[drainPosition] == EPosition.Horizontal) valueSecondary = valuesSecondaryDrainageTrench[drainHeight] as number;
                    break;
                default:
                    break;
            }
            row.values.push({ value: typeof valueSecondary != 'boolean' ? valueSecondary : valueSecondary ? i18n.t('yes') : i18n.t('no') });
        }
        rows.push(row);
        if (x == hasDrainagePiping && (valuesPrimaryDrainageTrench.hasDrainagePiping || valuesSecondaryDrainageTrench?.hasDrainagePiping)) {
            inputPipeDiamater.map(y => {
                let valuePrimary = valuesPrimaryDrainageTrench.hasDrainagePiping ? valuesPrimaryPipeDiamater[y as TInputDrainPipeDiameter] : undefined;
                let row = {
                    label: i18n.t(y, {
                        unitOfMeasure: drainPipeDiameterPD[y as TInputDrainPipeDiameter].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                    }),
                    values: [
                        {
                            value: typeof valuePrimary != 'boolean' ? valuePrimary : valuePrimary ? i18n.t('yes') : i18n.t('no'),
                        },
                    ],
                };
                if (valuesSecondaryPipeDiamater) {
                    let valueSecondary = valuesSecondaryDrainageTrench?.hasDrainagePiping ? valuesSecondaryPipeDiamater![y as TInputDrainPipeDiameter]! : undefined;
                    row.values.push({ value: typeof valueSecondary != 'boolean' ? valueSecondary : valueSecondary ? i18n.t('yes') : i18n.t('no') });
                }
                rows.push(row);
            });
        }
        if (x == hasDrainageChimney && (valuesPrimaryDrainageTrench.hasDrainageChimney || valuesSecondaryDrainageTrench?.hasDrainageChimney)) {
            inputDrainageChimney.map(y => {
                let valuePrimary = valuesPrimaryDrainageTrench.hasDrainageChimney ? valuesPrimaryDrainageChimney[y as TInputDrainageChimney] : undefined;
                if (y == configuration) {
                    valuePrimary = positionLabel[valuePrimary as EPosition];
                }
                let row = {
                    label: i18n.t(y, {
                        unitOfMeasure: drainageChimneyPD[y as TInputDrainageChimney].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                    }),
                    values: [
                        {
                            value: typeof valuePrimary != 'boolean' ? valuePrimary : valuePrimary ? i18n.t('yes') : i18n.t('no'),
                        },
                    ],
                };
                if (valuesSecondaryDrainageChimney) {
                    let valueSecondary = valuesSecondaryDrainageTrench?.hasDrainageChimney ? valuesSecondaryDrainageChimney![y as TInputDrainageChimney] : undefined;
                    if (y == configuration) {
                        valueSecondary = positionLabel[valueSecondary as EPosition];
                    }
                    row.values.push({ value: typeof valueSecondary != 'boolean' ? valueSecondary : valueSecondary ? i18n.t('yes') : i18n.t('no') });
                }
                rows.push(row);
            });
        }
    });

    return { columns, rows };
};

export const getInputFirstLayerSoil = (soil: ISoil): IInputSoil => ({
    soilType: soil.translations?.find(x => x.iso == getLanguage())?.name,
    layerNumber: soil.layerNumber!,
    frictionAngle: soil.soilPhysicalData![0].frictionAngle,
    drainedCohesion: soil.soilPhysicalData![0].drainedCohesion,
    undrainedCohesion: soil.soilPhysicalData![0].undrainedCohesion,
    unitWeight: soil.soilPhysicalData![0].unitWeight,
    thickness: soil.soilPhysicalData![0].thickness,
    saturatedUnitWeight: soil.soilPhysicalData![0].saturatedUnitWeight,
    effectivePorosity: soil.soilPhysicalData![0].effectivePorosity,
    drainedSoilPermeability: soil.soilPhysicalData![0].drainedSoilPermeability,
    permeabilityChangeUponRemodeling: soil.soilPhysicalData![0].permeabilityChangeUponRemodeling,
    remodeledSoilPermeability: soil.soilPhysicalData![0].remodeledSoilPermeability,
});

export const getInputNextLayerSoil = (soil: ISoil): IInputSoil => ({
    soilType: soil.translations?.find(x => x.iso == getLanguage())?.name,
    layerNumber: soil.layerNumber!,
    thickness: soil.soilPhysicalData![0].thickness,
    drainedSoilPermeability: soil.soilPhysicalData![0].drainedSoilPermeability,
    hydraulicConductivity: soil.soilPhysicalData![0].hydraulicConductivity,
});

export const getSoilTable = (soil: ISoil): ITable => {
    const project = redux.getState().project;
    let values = getInputFirstLayerSoil(soil);
    values.drainedSoilPermeability = (values.drainedSoilPermeability as number).toExponential(2);
    values.remodeledSoilPermeability = (values.remodeledSoilPermeability as number).toExponential(2);
    let columns = createColumns([i18n.t('firstLayerSoilData'), '']);
    let input = Object.keys(values).filter(x => (x != layerNumber && x != remodeledSoilPermeability) || (x == remodeledSoilPermeability && values.permeabilityChangeUponRemodeling));
    let rows = input.map(x => ({
        label: i18n.t(x, {
            unitOfMeasure: soilPD[x as TInputSoil].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
        }),
        values: [
            {
                value: typeof values[x as TInputSoil]! != 'boolean' ? values[x as TInputSoil]! : values[x as TInputSoil]! ? i18n.t('yes') : i18n.t('no'),
            },
        ],
    }));

    return { columns, rows };
};

export const getSoilLayersTable = (soils: ISoil[]): ITable => {
    const project = redux.getState().project;
    let input = soils.filter((x, i) => i !== 0).map(x => getInputNextLayerSoil(x));
    let columns = createColumns([
        i18n.t('additionalLayers'),
        i18n.t(soilType),
        i18n.t(thickness, { unitOfMeasure: soilPD[thickness]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] }),
        i18n.t(drainedSoilPermeability, { unitOfMeasure: soilPD[drainedSoilPermeability]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] }),
        i18n.t(hydraulicConductivity, { unitOfMeasure: soilPD[hydraulicConductivity]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] }),
    ]);

    let rows = input.map((x, i) => ({
        label: i18n.t('layer', { number: i + 2 }),
        values: [
            {
                value: x.soilType,
            },
            {
                value: x.thickness,
            },
            {
                value: (x.drainedSoilPermeability as number).toExponential(2),
            },
            {
                value: x.hydraulicConductivity,
            },
        ],
    }));

    return { columns, rows };
};

export const getLayersInSeriesTable = (soilInputData: ISoilInputData): ITable => {
    const project = redux.getState().project;
    let columns = createColumns(['']);
    let rows = [
        {
            label: i18n.t(layersInSeries, { unitOfMeasure: soilInputDataPD[layersInSeries]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] }),
            values: [
                {
                    value: (soilInputData!.layersInSeries as number).toExponential(2),
                },
            ],
        },
    ];
    return { columns, rows };
};
